import ReactECharts from 'echarts-for-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useApp } from '../../context/AppContext'
import type { Answer, MarketScore } from '../../lib/types'
import { pick } from '../../lib/utils'
import { ConfidenceBadge, DataTag } from '../Domain'
import { EuropeFlightMap } from '../maps/EuropeFlightMap'
import { Badge, Card, CardContent, CardHeader, CardTitle, NadiaLoader } from '../ui'

/**
 * Cuotas de mercado.
 *
 * Las barras son la CUOTA (medida de StatFin) y el marcadorInterior el score
 * del motor. Son dos cosas distintas y no se mezclan en un solo eje: poner las
 * dos en un grafico de barras daria a pensar que el score es un porcentaje de
 * algo medido, y no lo es.
 */
export function MarketChart({ markets, height = 280 }: { markets: MarketScore[]; height?: number }) {
  const { theme, locale } = useApp()
  const options = useMemo(() => {
    const dark = theme !== 'light'
    const text = dark ? '#cbd5e1' : '#334155'
    const grid = dark ? 'rgba(148,163,184,0.18)' : 'rgba(51,65,85,0.15)'
    const top = markets.slice(0, 12)
    return {
      backgroundColor: 'transparent',
      grid: { left: 92, right: 30, top: 12, bottom: 26 },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        valueFormatter: (v: number) => `${Math.round(v * 100)}%`,
      },
      xAxis: {
        type: 'value' as const,
        axisLabel: { color: text, fontSize: 10, formatter: (v: number) => `${Math.round(v * 100)}%` },
        splitLine: { lineStyle: { color: grid } },
      },
      yAxis: {
        type: 'category' as const,
        data: top.map((m) => pick(m.name, locale) || m.country).reverse(),
        axisLabel: { color: text, fontSize: 11 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar' as const,
          data: top
            .map((m) => m.share ?? 0)
            .reverse(),
          itemStyle: { borderRadius: [0, 4, 4, 0] },
          barMaxWidth: 18,
        },
      ],
    }
  }, [markets, theme, locale])

  if (!markets.length) return null
  return <ReactECharts option={options} style={{ height }} notMerge lazyUpdate />
}

export function WhoPanel({ answer, loading }: { answer?: Answer; loading: boolean }) {
  const { t } = useTranslation()
  const { locale } = useApp()

  if (loading) {
    return (
      <div className="space-y-4">
        <NadiaLoader
          minHeight="min-h-[280px]"
          message={t('loading.loadingMarkets', 'Cargando cuotas y mercados emisores...')}
          subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
        />
      </div>
    )
  }
  if (!answer) return null

  const withShare = answer.markets.filter((m) => m.share !== null)
  const leading = answer.markets[0]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {answer.headline[locale] ?? answer.headline.en}
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">{t('question.who.help')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ConfidenceBadge level={answer.confidence} score={answer.confidence_score} showScore />
          {answer.leading_budget_share !== null ? (
            <Badge variant="secondary">
              {t('who.budget')}: {(answer.leading_budget_share * 100).toFixed(0)}%
            </Badge>
          ) : null}
        </div>
      </div>

      <EuropeFlightMap markets={answer.markets} leadingMarket={answer.leading_market} />

      {withShare.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('who.marketShare')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <MarketChart markets={withShare} />
            <p className="text-muted-foreground text-xs">{t('who.market_shares_note')}</p>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {answer.markets.slice(0, 12).map((m) => (
          <Card key={m.country} className={m.country === answer.leading_market ? 'border-primary/50' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle>{pick(m.name, locale) || m.country}</CardTitle>
                  <p className="text-muted-foreground text-xs">{m.country}</p>
                </div>
                {m.country === answer.leading_market ? <Badge>#1</Badge> : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <div className="flex items-baseline justify-between">
                <span className="text-muted-foreground text-xs">{t('who.score')}</span>
                <span className="font-mono text-lg font-semibold">{m.score.toFixed(2)}</span>
              </div>
              {m.share !== null ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-muted-foreground text-xs">{t('who.marketShare')}</span>
                  <span className="font-mono text-lg">
                    {Math.round(m.share * 100)}%{' '}
                    <span className="text-muted-foreground text-[11px]">{t('who.shareOf')}</span>
                  </span>
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">{t('common.noData')}</p>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                <DataTag isEstimate={m.is_estimate} datasetIds={m.dataset_ids} />
                {m.contributions.slice(0, 3).map((c) => (
                  <Badge key={c.metric_key} variant="muted" className="font-mono text-[10px]">
                    {c.metric_key} {c.contribution.toFixed(2)}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {leading ? (
        <p className="text-muted-foreground text-[11px]">
          {t('common.sources')}: {answer.provenance_dataset_ids.join(', ')}
        </p>
      ) : null}
    </div>
  )
}
